// Карточка целиком: шапка, вкладки, содержимое вкладки.
//
// ЭТО И ЕСТЬ КОНСТРУКТОР. Компонент не знает ни одной сущности по имени: всё, что он умеет
// показать, приходит описанием (`ОписаниеКарточки`) и источником данных. Добавить сделке
// новое поле — правка одной строки в описании; добавить новую сущность — новый файл
// описания и строка в реестре. Ни строчки здесь при этом не меняется, и в этом весь смысл:
// сущностей впереди девять, а рук — одни.
//
// ПОЧЕМУ СПИСКИ ЧИТАЮТСЯ ЗДЕСЬ, А НЕ ВО ВКЛАДКАХ. Счётчики на полосе вкладок считаются по
// тем же спискам. Читать их во вкладке значит либо не иметь счётчиков, либо спрашивать
// источник дважды — ровно та ошибка, от которой в проекте стоит отдельный набор проверок
// (tests/nplus1.test.tsx).

import { useMemo, useState } from 'react'

import './cards.css'

import { CardHead } from './CardHead'
import { CardTabs } from './CardTabs'
import { DocsTab } from './DocsTab'
import { FeedTab } from './FeedTab'
import { HistoryTab } from './HistoryTab'
import { LinksTab } from './LinksTab'
import { MainTab } from './MainTab'
import type { ИсточникКарточек } from './source'
import { пусто } from './values'
import { useCardForm } from './useCardForm'
import { useИсточник } from './useSourceData'
import type {
  Действие,
  ЗаписьЖурнала,
  ЗаписьКарточки,
  КодВкладки,
  ОписаниеКарточки,
  Событие,
  Сущность,
  Файл,
} from './types'

interface Props {
  описание: ОписаниеКарточки
  запись: ЗаписьКарточки
  источник: ИсточникКарточек
  /** Права роли не дают править: кнопки убраны, поля показаны значениями. */
  безПравки?: boolean
  /** Описание другой сущности — нужно ссылкам на связанные карточки. */
  описаниеСущности?: (с: Сущность) => ОписаниеКарточки | undefined
  onДействие?: (д: Действие) => void
}

export function Card({
  описание, запись, источник, безПравки, описаниеСущности, onДействие,
}: Props) {
  const [вкладка, setВкладка] = useState<КодВкладки>('основное')

  const форма = useCardForm({
    описание,
    id: запись.id,
    исходные: запись.значения,
    ревизия: запись.ревизия,
    состояние: запись.состояние,
    источник,
    безЧерновика: безПравки,
  })

  const журнал = useИсточник<ЗаписьЖурнала[]>(
    () => источник.журнал(описание.сущность, запись.id),
    [источник, описание.сущность, запись.id, форма.ревизия],
  )
  const документы = useИсточник<Файл[]>(
    () => источник.документы(описание.сущность, запись.id),
    [источник, описание.сущность, запись.id],
  )
  const лента = useИсточник<Событие[]>(
    () => источник.лента(описание.сущность, запись.id),
    [источник, описание.сущность, запись.id],
  )

  const счётчики = useMemo(() => ({
    связи: описание.поля.filter((п) => п.вид === 'связь' && !пусто(форма.значения[п.код])).length,
    документы: документы.данные?.length,
    история: журнал.данные?.length,
    лента: лента.данные?.length,
  }), [описание.поля, форма.значения, документы.данные, журнал.данные, лента.данные])

  const найтиОписание = описаниеСущности ?? (() => undefined)

  return (
    <>
      <CardHead
        описание={описание}
        форма={форма}
        безПравки={безПравки}
        onДействие={onДействие}
      />

      <CardTabs текущая={вкладка} onВыбор={setВкладка} счётчики={счётчики} />

      <div id={`panel-${вкладка}`} role="tabpanel" aria-labelledby={`tab-${вкладка}`}>
        {/* Поля рисуются на ЛЮБОЙ вкладке, которой их отдало описание: роли и договор
            живут на «Связях», вложения — на «Документах». Иначе описанное поле оказалось
            бы недоступным, и заметить это можно было бы только по жалобе. */}
        <MainTab
          описание={описание}
          форма={форма}
          источник={источник}
          безПравки={безПравки}
          путьСущности={(с, id) => найтиОписание(с)?.путь(id) ?? '#'}
          вкладка={вкладка}
        />
        {вкладка === 'связи' && (
          <LinksTab
            описание={описание}
            значения={форма.значения}
            id={запись.id}
            источник={источник}
            описаниеСущности={найтиОписание}
          />
        )}
        {вкладка === 'документы' && <DocsTab ответ={документы} />}
        {вкладка === 'история' && <HistoryTab ответ={журнал} />}
        {вкладка === 'лента' && <FeedTab ответ={лента} />}
      </div>
    </>
  )
}
